import './styling/travelStyling.scss';

import { FC } from 'react';
import { Control, FieldErrors } from 'react-hook-form';
import { Box, Label, Title } from '@gjensidige/builders-components';

import {
  changeReturnTimeDateOptions,
  insuranceUserOptions,
  travelPurposeOptions,
  yesOrNo,
} from '../../../constants';
import { EquinorTravelValues } from '../../../utils/types';
import Datepicker from '../../../components/Datepicker';
import Input from '../../../components/Input';
import InputGroup from '../../../components/InputGroup';
import Textarea from '../../../components/Textarea';
import { TravelHeader } from '../components/TravelHeader';
import { PersonDetails } from './PersonDetails';

interface MedicalProps {
  control: Control<EquinorTravelValues>;
  errors: FieldErrors<EquinorTravelValues>;
}

const Medical: FC<MedicalProps> = ({ control, errors }) => {
  return (
    <>
      <TravelHeader
        headerTitle="Medical"
        description="You seek compensation related to an illness or injury, such as hospital expenses."
      />
      <PersonDetails control={control} errors={errors} />
      <Box variant="form-section" className="form-section">
        <Title as="h4" size="5">
          Claims details
        </Title>
        <InputGroup
          name="injuredParty"
          control={control}
          labelText="Who was ill/injured?"
          options={insuranceUserOptions}
          type="radio"
          variant="column"
          tracking={false}
          error={errors.injuredParty}
        />
        <Label>
          If it is not you that was ill/injured, add the name and birtdate of the one affected –
          Optional
        </Label>
        <Input
          name="affectedFullName"
          control={control}
          labelText="Full name"
          type="text"
          tracking={false}
          error={errors.affectedFullName}
        />
        <Input
          name="affectedDateOfBirth"
          control={control}
          labelText="Date of birth"
          type="text"
          tracking={false}
          error={errors.affectedDateOfBirth}
        />
        <Datepicker
          name="incidentDate"
          control={control}
          labelText="When did the illness or injury occur? (dd/mm/yyyy)"
          format="dd/MM/yyyy"
          formatDayJs="DD/MM/YYYY"
          error={errors.incidentDate}
        />
        <Input
          name="country"
          control={control}
          labelText="In which country did the incident occur"
          type="text"
          tracking={false}
          error={errors.country}
        />
        <InputGroup
          name="medical.reportedToGjTravelAssistance"
          control={control}
          labelText="Is the incident also reported to Gjensidige Travel Assistance?"
          helpText="Gjensidige Travel Assistance is our Alarm Center"
          options={yesOrNo}
          type="radio"
          variant="column"
          tracking={false}
          error={errors.medical?.reportedToGjTravelAssistance}
        />
        <Input
          name="medical.claimNumberGjTravelAssistance"
          control={control}
          labelText="If yes, please add the Claim number from Gjensidige Travel Assistance - optional"
          type="text"
          tracking={false}
          error={errors.medical?.claimNumberGjTravelAssistance}
        />
        <InputGroup
          name="travelPurpose"
          control={control}
          labelText="What was the purpose of the trip?"
          options={travelPurposeOptions}
          type="radio"
          variant="column"
          tracking={false}
          error={errors.travelPurpose}
        />
        <Datepicker
          name="tripStartBookedDate"
          control={control}
          labelText="When did the trip start? (dd/mm/yyyy)"
          format="dd/MM/yyyy"
          formatDayJs="DD/MM/YYYY"
          error={errors.tripStartBookedDate}
        />
        <Datepicker
          name="tripEndCancelledDate"
          control={control}
          labelText="When did the trip end? (dd/mm/yyyy)"
          helpText="If you are still traveling, choose your planned trip end date"
          format="dd/MM/yyyy"
          formatDayJs="DD/MM/YYYY"
          error={errors.tripEndCancelledDate}
        />
        <InputGroup
          name="medical.changeReturnTimeDate"
          control={control}
          labelText="Did you need to change the time or date for your return home?"
          options={changeReturnTimeDateOptions}
          type="radio"
          variant="column"
          tracking={false}
          error={errors.medical?.changeReturnTimeDate}
        />
        <Textarea
          name="detailedIncidentDescription"
          control={control}
          labelText="Describe what happened in detail"
          maxWarning={500}
          error={errors.detailedIncidentDescription}
        />
        <Input
          name="compensation"
          control={control}
          labelText="Add the amount you are seeking compensation for"
          type="text"
          tracking={false}
          error={errors.compensation}
        />
        <Textarea
          name="detailedDescriptionCompensation"
          control={control}
          labelText="Provide a detailed description of what kind of expenses you are seeking compensation for"
          maxWarning={500}
          error={errors.detailedDescriptionCompensation}
        />
      </Box>
    </>
  );
};

export default Medical;
